import Head from 'expo-router/head';
import { useCallback, useMemo, useState } from 'react';
import { ScrollView, Text, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useTranslation } from 'react-i18next';

import { listSeedScript } from '@/components/detail/list-seed';
import { EventCard } from '@/components/riviera/event-card';
import { EventSliceChips } from '@/components/riviera/event-slice-chips';
import { HubFilterStatus, HubFilters, eventFilterGroups } from '@/components/riviera/hub-filters';
import { LoadMoreButton } from '@/components/riviera/load-more-button';
import {
  CityChipRow,
  Faq,
  LinkGroup,
  LinkRow,
  MoreLink,
  SectionHeading,
  StatChip,
  landingStyles as styles,
} from '@/components/riviera/landing-parts';
import { SiteFooter } from '@/components/riviera/site-footer';
import { SiteHeader } from '@/components/riviera/site-header';
import { Fonts, Spacing } from '@/constants/theme';
import { useCityListingPages } from '@/hooks/use-city-listing-pages';
import { useEvents } from '@todo/core/hooks/use-events';
import { useFilteredHub } from '@todo/core/hooks/use-filtered-hub';
import { useTheme } from '@/hooks/use-theme';
import {
  MIN_EVENTS_PER_CITY_PAGE,
  TOP_EVENTS_ON_CITY_PAGE,
  eventCityStats,
  prettyVenue,
  type EventCityStats,
} from '@todo/core/city-listings';
import { APP_TZ } from '@todo/core/date-range';
import { cities } from '@/lib/events-data';
import { DEFAULT_EVENT_FILTERS, isDefaultFilters, type EventFilters } from '@todo/core/hub-filters';
import { filteredEventsFetcher, type FetchPage } from '@todo/core/hub-pages';
import { cityPageCopy } from '@/lib/i18n/city-content';
import { cityEventsCopy, cityInPhrase, cityToursCopy } from '@/lib/i18n/city-listings-content';
import { localizedTitle } from '@todo/core/i18n/event-content';
import { usableImageUrl } from '@todo/core/image-url';
import { formatDateCompact } from '@todo/core/i18n/format';
import type { Locale } from '@todo/core/i18n/locales';
import { useLocale } from '@todo/core/i18n/use-locale';
import {
  CITY_EVENT_CATEGORY_PAGES_SEED_KEY,
  CITY_LISTING_PAGES_SEED_KEY,
  cityEventsPageQuery,
  cityEventsPageSeedKey,
} from '@todo/core/list-queries';
import {
  SITE_NAME,
  absoluteUrl,
  breadcrumbJsonLd,
  faqPageJsonLd,
  hreflangLinks,
  itemListJsonLd,
  richResultImage,
  seoTitle,
  truncate,
} from '@todo/core/seo';
import type { DbEvent } from '@todo/core/supabase';

type Translate = (key: string, options?: Record<string, unknown>) => string;

/** How many venues the "Venues" block and the venues FAQ answer name. */
const TOP_VENUES = 8;
const FAQ_VENUES = 5;

// Event prices are EUR (Fnac Spectacles France); same "€12" format as EventCard.
function eur(value: number): string {
  return `€${Math.round(value)}`;
}

function categoryName(t: Translate, id: string): string {
  return t(`categories.${id}`, { defaultValue: t('categories.other') });
}

function categoryNoun(t: Translate, id: string): string {
  return t(`categoryNouns.${id}`, { defaultValue: t('categoryNouns.other') });
}

/**
 * The city FAQ, built from the page's own numbers (what's on, when, where,
 * from what price) so every city's answers differ.
 */
function eventsFaq(stats: EventCityStats, inCity: string, locale: Locale, t: Translate) {
  if (stats.total === 0) return [];
  const sep = t('cityEvents.listSeparator');
  const faq: { q: string; a: string }[] = [];

  if (stats.firstStart && stats.lastStart) {
    faq.push({
      q: t('cityEvents.faq.whatQ', { inCity }),
      a: t('cityEvents.faq.whatA', {
        inCity,
        count: stats.total,
        first: formatDateCompact(stats.firstStart, APP_TZ, locale),
        last: formatDateCompact(stats.lastStart, APP_TZ, locale),
        list: stats.categories.map((c) => `${categoryNoun(t, c.id)} (${c.count})`).join(sep),
      }),
    });
  }

  if (stats.venues.length > 0) {
    faq.push({
      q: t('cityEvents.faq.venuesQ', { inCity }),
      a: t('cityEvents.faq.venuesA', {
        inCity,
        list: stats.venues
          .slice(0, FAQ_VENUES)
          .map((venue) => `${venue.name} (${venue.count})`)
          .join(sep),
      }),
    });
  }

  faq.push({
    q: t('cityEvents.faq.ticketsQ', { inCity }),
    a:
      stats.minPrice != null
        ? t('cityEvents.faq.ticketsPriceA', { inCity, price: eur(stats.minPrice) })
        : t('cityEvents.faq.ticketsA', { inCity }),
  });

  return faq;
}

/** The page has no "Show more" while unfiltered: it lists the whole city already. */
const NO_MORE_SEED_PAGES: FetchPage<DbEvent> = async () => [];

/**
 * The /[lang]/[city]/events landing page — every upcoming event in one city.
 * Built for "events in nice" / "agenda nice" / "афиша ниццы" searches, which
 * the city guide (/[city], "things to do") and the regional /events hub only
 * half-answer: the guide shows 24 events, and the hub lists the whole region
 * (its city chips link here). This page gives the query
 * a URL of its own, with per-city substance (hand-written intro, counts by
 * type, venues, a data-driven FAQ) — and gives every event detail page a
 * crawlable inbound link from its city.
 */
export function CityEventsScreen({ cityId }: { cityId: string }) {
  const theme = useTheme();
  const { t } = useTranslation();
  const { locale } = useLocale();
  const city = cities.find((c) => c.id === cityId);
  const copy = city ? cityEventsCopy(locale, city.id) : undefined;
  const inCity = city ? cityInPhrase(locale, city.id) : undefined;

  // Always called (rules of hooks). An unknown city id only arrives via a bad
  // client-side navigation — generateCityEventsStaticParams never emits one —
  // and just returns no rows.
  const { events, loading, error } = useEvents(
    cityEventsPageQuery(cityId),
    city ? cityEventsPageSeedKey(city.id, locale) : undefined,
  );
  const pages = useCityListingPages();
  const stats = eventCityStats(events);

  // Filters + sort, after hydration only. The seed holds only each show's
  // soonest date (and images for the top cards), so a filtered list comes from
  // event_shows_filtered — exact for a recurring show's every date — and
  // replaces the top cards + link list, which the static HTML always keeps.
  const [filters, setFilters] = useState<EventFilters>(DEFAULT_EVENT_FILTERS);
  const fetchFiltered = useMemo(
    () => filteredEventsFetcher(cityId, cityEventsPageQuery(cityId).untilDays ?? 730),
    [cityId],
  );
  const filtered = useFilteredHub(
    events,
    NO_MORE_SEED_PAGES,
    filters,
    isDefaultFilters(filters, DEFAULT_EVENT_FILTERS),
    fetchFiltered,
  );
  const setFilter = useCallback(
    (id: string, value: string) => setFilters((prev) => ({ ...prev, [id]: value })),
    [],
  );
  const resetFilters = useCallback(() => setFilters(DEFAULT_EVENT_FILTERS), []);

  if (!city || !copy || !inCity) {
    return (
      <View style={[styles.root, { backgroundColor: theme.background }]}>
        <Head key={locale}>
          <title>{`${t('common.notFound')} — ${SITE_NAME}`}</title>
          <meta name="robots" content="noindex, follow" />
        </Head>
        <SafeAreaView edges={['top']} style={{ backgroundColor: theme.card }}>
          <SiteHeader />
        </SafeAreaView>
        <Text style={[styles.status, { color: theme.mutedForeground, paddingHorizontal: Spacing.three }]}>
          {t('common.notFound')}
        </Text>
      </View>
    );
  }

  const path = `${city.id}/events`;
  const canonical = absoluteUrl(locale, path);
  const title = seoTitle(copy.seoTitle);
  const sep = t('cityEvents.listSeparator');
  const metaDescription = truncate(
    stats.total > 0
      ? t('cityEvents.metaDescription', {
          inCity,
          count: stats.total,
          list: stats.categories
            .slice(0, 3)
            .map((c) => categoryNoun(t, c.id))
            .join(sep),
        })
      : copy.intro[0],
  );
  const top = events.slice(0, TOP_EVENTS_ON_CITY_PAGE);
  const ogImage = richResultImage(top.find((event) => usableImageUrl(event.image_url))?.image_url);
  // Too few events to be worth indexing — see MIN_EVENTS_PER_CITY_PAGE. Only
  // once loaded, so a page still fetching client-side isn't flagged.
  const thin = !loading && events.length < MIN_EVENTS_PER_CITY_PAGE;
  const faq = eventsFaq(stats, inCity, locale, t);
  const cityGuide = cityPageCopy(city.id, locale);
  const toursCopy = pages.tours.includes(city.id) ? cityToursCopy(locale, city.id) : undefined;

  // Every event, grouped by category in the stats' order (most events first);
  // within a group, soonest first (the RPC's order).
  const groups = stats.categories.map((category) => ({
    id: category.id,
    rows: events.filter((event) => (event.category_id ?? 'other') === category.id),
  }));

  const statsLine =
    stats.total > 0
      ? [
          t('cityEvents.stats', { count: stats.total }),
          stats.minPrice != null ? t('cityEvents.priceStat', { price: eur(stats.minPrice) }) : null,
        ]
          .filter(Boolean)
          .join(' · ')
      : null;

  return (
    <View style={[styles.root, { backgroundColor: theme.background }]}>
      {/* key forces a fresh mount on locale change — see the comment in
          app/[lang]/_layout.tsx for why. */}
      <Head key={locale}>
        <title>{title}</title>
        <meta name="description" content={metaDescription} />
        {thin ? <meta name="robots" content="noindex, follow" /> : null}
        <link rel="canonical" href={canonical} />
        {hreflangLinks(path).map((link) => (
          <link key={link.hrefLang} rel="alternate" hrefLang={link.hrefLang} href={link.href} />
        ))}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={canonical} />
        {ogImage ? <meta property="og:image" content={ogImage} /> : null}
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={metaDescription} />
        {ogImage ? <meta name="twitter:image" content={ogImage} /> : null}
        <script type="application/ld+json">
          {JSON.stringify(
            breadcrumbJsonLd(locale, [
              { name: t('nav.cities'), path: 'cities' },
              { name: city.name, path: city.id },
              { name: copy.title },
            ]),
          )}
        </script>
        {top.length > 0 ? (
          <script type="application/ld+json">
            {JSON.stringify(
              itemListJsonLd(
                top.map((event) => ({
                  name: localizedTitle(event, locale),
                  url: absoluteUrl(locale, `events/${event.show_slug}`),
                })),
                canonical,
              ),
            )}
          </script>
        ) : null}
        {faq.length > 0 ? (
          <script type="application/ld+json">{JSON.stringify(faqPageJsonLd(faq))}</script>
        ) : null}
        {/* Baked rows so the client's first render matches this static HTML. */}
        {listSeedScript(cityEventsPageSeedKey(city.id, locale))}
        {listSeedScript(CITY_LISTING_PAGES_SEED_KEY)}
        {listSeedScript(CITY_EVENT_CATEGORY_PAGES_SEED_KEY)}
      </Head>
      <SafeAreaView edges={['top']} style={{ backgroundColor: theme.card }}>
        <SiteHeader />
      </SafeAreaView>
      <ScrollView style={{ flex: 1 }}>
        <View style={styles.centered}>
          <View style={styles.page}>
            {/* role="heading" + aria-level renders a real <h1> on web (via
                react-native-web) while staying valid on native. */}
            <Text
              role="heading"
              aria-level={1}
              style={[styles.heading, { color: theme.text, fontFamily: Fonts?.serif }]}>
              {copy.title}
            </Text>
            {statsLine ? (
              <Text style={[styles.stats, { color: theme.gold }]}>{statsLine}</Text>
            ) : null}
            {copy.intro.map((paragraph, index) => (
              <Text key={index} style={[styles.intro, { color: theme.text }]}>
                {paragraph}
              </Text>
            ))}

            {/* This weekend / today / this week, and the city's category pages. */}
            <EventSliceChips cityId={city.id} active={null} inCity={inCity} />

            {pages.events.length > 1 ? (
              <CityChipRow
                heading={t('cityEvents.citiesHeading')}
                chips={pages.events.map((id) => ({
                  key: id,
                  label: cities.find((c) => c.id === id)?.name ?? id,
                  active: id === city.id,
                  href: { pathname: '/[lang]/[city]/events', params: { lang: locale, city: id } },
                }))}
              />
            ) : null}

            {loading ? (
              <Text style={[styles.status, { color: theme.mutedForeground }]}>
                {t('cityEvents.loading')}
              </Text>
            ) : error ? (
              <Text style={[styles.status, { color: theme.destructive }]}>{error}</Text>
            ) : events.length === 0 ? (
              <Text style={[styles.status, { color: theme.mutedForeground }]}>
                {t('cityEvents.empty')}
              </Text>
            ) : (
              <>
                <View style={styles.section}>
                  <HubFilters
                    groups={eventFilterGroups(
                      filters,
                      t,
                      stats.categories.map((category) => category.id),
                    )}
                    onChange={setFilter}
                    onReset={resetFilters}
                  />
                  <HubFilterStatus
                    filtered={filtered.filtered}
                    total={filtered.total}
                    loading={filtered.loading}
                    error={filtered.error}
                    countLabel={(count) => t('hubFilters.eventsCount', { count })}
                    onRetry={filtered.retry}
                    onReset={resetFilters}
                  />
                </View>

                {filtered.filtered ? (
                  <View
                    aria-busy={filtered.loading}
                    style={[styles.section, filtered.loading && { opacity: 0.5 }]}>
                    <View style={styles.grid}>
                      {filtered.rows.map((event, index) => (
                        <EventCard key={event.id} event={event} eager={index < 4} />
                      ))}
                      {filtered.more.extra.map((event) => (
                        <EventCard key={event.id} event={event} eager={false} />
                      ))}
                    </View>
                    {filtered.more.canLoadMore && !filtered.loading ? (
                      <LoadMoreButton
                        onPress={filtered.more.loadMore}
                        loading={filtered.more.loading}
                        error={filtered.more.error}
                      />
                    ) : null}
                  </View>
                ) : (
                  <>
                    <View
                      aria-busy={filtered.loading}
                      style={[styles.section, filtered.loading && { opacity: 0.5 }]}>
                      <SectionHeading>{t('cityEvents.nextHeading', { inCity })}</SectionHeading>
                      <View style={styles.grid}>
                        {/* First row is above the fold on desktop — eager, as on /events. */}
                        {top.map((event, index) => (
                          <EventCard key={event.id} event={event} eager={index < 4} />
                        ))}
                      </View>
                    </View>

                    <View style={styles.section}>
                      <SectionHeading>{t('cityEvents.allHeading', { inCity })}</SectionHeading>
                      {groups.map((group) => (
                        <LinkGroup
                          key={group.id}
                          heading={categoryName(t, group.id)}
                          count={group.rows.length}
                          showAllLabel={t('cityEvents.showAll', { count: group.rows.length })}
                          showFewerLabel={t('cityEvents.showFewer')}
                          renderRow={(index, hidden) => (
                            <EventRow
                              key={group.rows[index].id}
                              event={group.rows[index]}
                              locale={locale}
                              hidden={hidden}
                              t={t}
                            />
                          )}
                        />
                      ))}
                    </View>
                  </>
                )}

                {stats.venues.length > 0 ? (
                  <View style={styles.section}>
                    <SectionHeading>{t('cityEvents.venuesHeading')}</SectionHeading>
                    <View style={styles.chipsRow}>
                      {stats.venues.slice(0, TOP_VENUES).map((venue) => (
                        <StatChip key={venue.name} label={`${venue.name} · ${venue.count}`} />
                      ))}
                    </View>
                  </View>
                ) : null}
              </>
            )}

            <Faq heading={t('cityEvents.faqHeading', { inCity })} entries={faq} />

            <View style={styles.moreLinks}>
              {cityGuide ? (
                <MoreLink
                  label={cityGuide.title}
                  href={{ pathname: '/[lang]/[city]', params: { lang: locale, city: city.id } }}
                />
              ) : null}
              {toursCopy ? (
                <MoreLink
                  label={toursCopy.title}
                  href={{ pathname: '/[lang]/[city]/tours', params: { lang: locale, city: city.id } }}
                />
              ) : null}
              <MoreLink
                label={t('cityEvents.allEvents')}
                href={{ pathname: '/[lang]/events', params: { lang: locale } }}
              />
            </View>
          </View>
        </View>
        <SiteFooter />
      </ScrollView>
    </View>
  );
}

export function EventRow({
  event,
  locale,
  hidden,
  t,
}: {
  event: DbEvent;
  locale: Locale;
  hidden: boolean;
  t: Translate;
}) {
  const facts = [
    formatDateCompact(event.start_ts, event.tz || APP_TZ, locale),
    (event.date_count ?? 1) > 1 ? t('events.moreDates', { count: (event.date_count ?? 1) - 1 }) : null,
    event.venue_name ? prettyVenue(event.venue_name) : null,
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <LinkRow
      href={{ pathname: '/[lang]/events/[id]', params: { lang: locale, id: event.show_slug } }}
      title={localizedTitle(event, locale)}
      facts={facts}
      hidden={hidden}
    />
  );
}
